import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import {
  Accordion,
  Button,
  Form,
  Grid,
  Icon,
  List,
  Segment,
} from "semantic-ui-react";
import sortBy from "lodash/sortBy";
import moment from "moment-timezone";
import PropTypes from "prop-types";
import Dropzone from "react-dropzone";

import CustomAttachmentForm from "../forms/CustomAttachmentForm";
import EmailableContactsModal from "../components/EmailableContactsModal";
import EmailDeliveryTime from "../forms/EmailDeliveryTime";
import RuvixxForm from "../../../components/RuvixxForm";
import OutreachFiltersForm from "../../../components/OutreachFiltersForm";
import EmailScheduleSendOptionForm from "../forms/EmailScheduleSendOptionForm";
import { getUserTimeZone } from "../../../components/helpers";

import CampaignService from "../../../services/Campaign";
import CampaignScheduleService from "../../../services/CampaignScheduleService";
import EmailTemplateService from "../../../services/EmailTemplate";
import EmailFooterService from "../../../services/EmailFooters";
import EmailSignatureService from "../../../services/EmailSignatures";
import EntityService from "../../../services/Entity";
import CustomEmailDomainsService from "services/CustomEmailDomains";
import TagService from "../../../services/Tag";
import UploadManager from "../../../components/UploadManager";
import UploadProgressRing from "../../../components/UploadProgressRing";
import SmtpAccountService from "../../../services/SmtpAccounts";
import EmailProviderService from "../../../services/EmailProviders";
import UserService from "../../../services/User";
import CONSTANTS from "../../../constants/Constants";
import { v4 as uuidv4 } from "uuid";
import ConstantContactAccountsService from "../../../services/ConstantContactAccounts";
import Validate from "../../../components/Validate";

const nullOption = {
  key: null,
  value: null,
  text: "None",
};

const templateDisabled = (template, provider, emailTemplateId) => {
  const { provider_list: providerList } = template.info;
  if (!provider) return false;
  if (!providerList) return false;
  let disabled;
  switch (provider) {
    case CONSTANTS.EMAIL_SERVICES.MAILGUN:
    case CONSTANTS.EMAIL_SERVICES.SENDGRID:
      disabled = !providerList.mailgun_sendgrid;
      break;
    case CONSTANTS.EMAIL_SERVICES.CONSTANT_CONTACT:
      disabled = !providerList.constant_contact;
      break;
    case CONSTANTS.EMAIL_SERVICES.SMTP:
      disabled = !providerList.smtp;
      break;
    default:
      disabled = false;
  }
  return disabled;
};

const convertLocalDisplayToUTC = (localMoment, timezone) => {
  if (!localMoment) return null;

  const displayedTime = localMoment.format("YYYY-MM-DD HH:mm:ss");
  return moment.tz(displayedTime, timezone).utc();
};

const adjustDisplayTimeForTimezoneChange = (
  currentDisplayMoment,
  oldTimezone,
  newTimezone
) => {
  if (!currentDisplayMoment) return null;

  // Get the displayed time string and interpret it in the old timezone
  const displayedTime = currentDisplayMoment.format("YYYY-MM-DD HH:mm:ss");
  const utcTime = moment.tz(displayedTime, oldTimezone).utc();

  // Convert to new timezone and create a UTC moment with that local time value
  const newDisplayTime = utcTime.tz(newTimezone);
  return moment.utc(newDisplayTime.format("YYYY-MM-DD HH:mm:ss"));
};

const CampaignScheduleForm = ({
  campaignId,
  onClose,
  onSuccess,
  newSchedule,
  scheduleId: initialScheduleId,
  isClone,
}) => {
  const [uploadManager, setUploadManager] = useState(undefined);
  const [scheduleId, setScheduleId] = useState(initialScheduleId);
  const [scheduleName, setScheduleName] = useState("");
  const [status, setStatus] = useState(false);
  const [sendNow, setSendNow] = useState(true);
  const [emailDeliveryTime, setEmailDeliveryTime] = useState(null);
  const [timeZone, setTimeZone] = useState(getUserTimeZone());
  const [timeZones, setTimeZones] = useState([]);
  const [emailTemplateId, setEmailTemplateId] = useState(undefined);
  const [conditions, setConditions] = useState([]);
  const [customAttachments, setCustomAttachments] = useState([]);
  const [info, setInfo] = useState({});
  const [campaignStatusOptions, setCampaignStatusOptions] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [emailTemplateOptions, setEmailTemplateOptions] = useState([]);
  const [files, setFiles] = useState([]);
  const [entityTagOptions, setEntityTagOptions] = useState([]);
  const [callTagOptions, setCallTagOptions] = useState([]);
  const [contactTagOptions, setContactTagOptions] = useState([]);
  const [senderName, setSenderName] = useState("noreply");
  const [fromAddress, setFromAddress] = useState("");
  const [cc, setCc] = useState("");
  const [bcc, setBcc] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [submitErrors, setSubmitErrors] = useState({});
  const errorMessages = { ...fieldErrors, ...submitErrors };

  const [ccMenuOpen, setCcMenuOpen] = useState(false);
  const [ccAccountManagers, setCcAccountManagers] = useState(false);
  const [ccExternalAccountManagers, setCcExternalAccountManagers] =
    useState(false);
  const [fromDomain, setFromDomain] = useState("email.domain.com");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSendingToAllContacts, setIsSendingToAllContacts] = useState(false);
  const [validateDisabled, setValidateDisabled] = useState(true);
  const [providerIsSMTP, setProviderIsSMTP] = useState(false);
  const [providerIsOutlook, setProviderIsOutlook] = useState(false);
  const [providerIsConstantContact, setProviderIsConstantContact] =
    useState(false);
  const [providerIsMailgun, setProviderIsMailgun] = useState(false);
  const [constantContactAccounts, setConstantContactAccounts] = useState([]);
  const [constantContactAccountId, setConstantContactAccountId] =
    useState(null);
  const [provider, setProvider] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [smtpOptions, setSmtpOptions] = useState([]);
  const [smtp_config_id, setSmtpConfigId] = useState(undefined);
  const [selectedTemplates, setSelectedTemplates] = useState([]);
  const [emailServices, setEmailServices] = useState([]);
  const [footerOptions, setFooterOptions] = useState([]);
  const [footerId, setFooterId] = useState(null);
  const [signatureOptions, setSignatureOptions] = useState([]);
  const [signatureId, setSignatureId] = useState(null);
  const [outlookEmailAliases, setOutlookEmailAliases] = useState([]);
  const [outlookAliasSelected, setOutlookAliasSelected] = useState(null);
  const [mailgunDomainSelected, setMailgunDomainSelected] = useState(null);
  const [users, setUsers] = useState([]);
  const [entityOptions, setEntityOptions] = useState([]);
  const [filterReady, setFilterReady] = useState(false);
  const [checkFromField, setCheckFromField] = useState(false);
  const [mailgunDomainOptions, setMailgunDomainOptions] = useState([]);
  const [sendToAllEmailAddresses, setSendToAllEmailAddresses] = useState(false);
  const [runOnce, setRunOnce] = useState(false);
  const [runUntil, setRunUntil] = useState(false);
  const [runContinuously, setRunContinuously] = useState(true);
  const [runDateLimit, setRunDateLimit] = useState(null);
  const [runDateLimitTimeZone, setRunDateLimitTimeZone] = useState(null);

  const userOptions = useMemo(() => {
    return users.map(user => ({
      key: user.id,
      value: user.id,
      text: user.full_name,
    }));
  }, [users]);

  const setupTimezones = async () => {
    const timeZonesNames = moment.tz.names();
    const usPacificNewIdx = timeZonesNames.indexOf("US/Pacific-New");
    if (usPacificNewIdx !== -1) timeZonesNames.splice(usPacificNewIdx, 1);
    const options = timeZonesNames.map(zone => {
      let offset = moment.tz(zone).format("Z");
      return { text: zone + " (" + offset + ")", key: zone, value: zone };
    });
    setTimeZones(options);
  };

  const fetchOutlookAliases = async () => {
    const outlookEmailAliases =
      await EmailProviderService.getEmailAliasesForOutlook();
    setOutlookEmailAliases(outlookEmailAliases);
  };

  const fetchEmailServices = async () => {
    const emailServices = await EmailProviderService.getEmailProviders(
      true,
      true
    );
    setEmailServices(emailServices);
  };

  const fetchEmailServicesOptions = async provider => {
    switch (provider) {
      case CONSTANTS.EMAIL_SERVICES.SMTP:
        await fetchSmtpDetails();
        break;
      case CONSTANTS.EMAIL_SERVICES.OUTLOOK:
        await fetchOutlookAliases();
        break;
      case CONSTANTS.EMAIL_SERVICES.CONSTANT_CONTACT:
        await fetchConstantContactAccounts();
        break;
      case CONSTANTS.EMAIL_SERVICES.MAILGUN:
        await fetchMailgunDomains();
        break;
      default:
        break;
    }
  };

  const fetchSmtpDetails = async () => {
    let smtpOptions = await SmtpAccountService.getSmtpAccounts();
    smtpOptions = smtpOptions.map(({ id, return_addr }) => ({
      key: id,
      text: return_addr,
      value: id,
    }));
    setSmtpOptions(smtpOptions);
  };

  const handleAccountChange = (event, { value }) => {
    if (!value) {
      return;
    }
    const smtpAccount = smtpOptions
      .filter(opt => opt.value === value)
      .map(({ value, text }) => ({
        id: value,
        return_addr: text,
      }))[0];
    setSmtpConfigId(value);
    setFromAddress(smtpAccount.return_addr);
  };

  const fetchEntities = async (search = null) => {
    const filters = {};
    if (search) {
      filters["search_query"] = search;
    }
    const entities = await EntityService.getEntitiesForFilters(filters);
    let entity_options = entities.map(entity => ({
      text: entity.name,
      key: entity.id,
      value: entity.id,
    }));
    setEntityOptions(entity_options);
  };

  const fetchConstantContactAccounts = async () => {
    const accounts = await ConstantContactAccountsService.getForFilters();
    let accountOptions = accounts.map(account => {
      const authenticated = account.authentication_status === "authenticated";
      return {
        text: account.name + (authenticated ? "" : " (Not Authenticated)"),
        key: account.id,
        value: account.id,
        disabled: !authenticated,
      };
    });
    setConstantContactAccounts(accountOptions);
  };

  const fetchMailgunDomains = async () => {
    const domains = await CustomEmailDomainsService.getVerifiedDomains();
    const domainOptions = domains.map(domain => ({
      key: domain.id,
      value: domain.name,
      text: domain.name,
    }));
    const defaultDomain = domains.find(domain => domain.is_default)?.name;

    return {
      domainOptions,
      defaultDomain,
    };
  };

  const fetchSchedule = async (campaignId, scheduleId, isClone = false) => {
    const schedule = await CampaignScheduleService.getSchedule(
      campaignId,
      scheduleId
    );

    const isMailgun =
      schedule.email_service === CONSTANTS.EMAIL_SERVICES.MAILGUN;

    const isConstantContactOrSMTP =
      schedule.email_service === CONSTANTS.EMAIL_SERVICES.CONSTANT_CONTACT ||
      schedule.email_service === CONSTANTS.EMAIL_SERVICES.SMTP;
    let fromAddress = schedule.info.from_address;
    let mailgunDomainSelected = null;
    let mailgunDomainOptions = [];

    if (isMailgun) {
      const { domainOptions, defaultDomain } = await fetchMailgunDomains();
      mailgunDomainOptions = domainOptions;

      if (fromAddress && fromAddress.includes("@")) {
        [fromAddress, mailgunDomainSelected] = fromAddress.split("@");
      }

      if (
        !mailgunDomainSelected ||
        !mailgunDomainOptions.find(opt => opt.value === mailgunDomainSelected)
      ) {
        mailgunDomainSelected = defaultDomain;
      }
    }

    const clonedConditions = isClone
      ? [...(schedule.conditions || [])]
      : schedule.conditions || [];
    const shouldSendToAllContacts = clonedConditions.length === 0;
    const emailService = schedule.email_service || "";

    setProvider(emailService);
    if (emailService) {
      await fetchEmailServicesOptions(emailService);
    }
    setScheduleId(isClone ? undefined : scheduleId);
    setSendNow(schedule.info.send_now);
    setEmailDeliveryTime(
      getEmailDeliveryTime(schedule.email_delivery_time, schedule.time_zone)
    );
    setTimeZone(schedule.time_zone || "UTC");
    setSenderName(schedule.sender_name || "noreply");
    setFromAddress(fromAddress || "");
    setCc(schedule.info.cc || "");
    setBcc(schedule.info.bcc || "");
    setCcAccountManagers(schedule.info.cc_account_managers || false);
    setCcExternalAccountManagers(
      schedule.info.cc_external_account_managers || false
    );
    setFromDomain("email.domain.com");
    setStatus(isClone ? false : schedule.status || false);
    setEmailTemplateId(schedule.email_template_id || undefined);
    setFooterId(schedule.info.footer_id);
    setSignatureId(schedule.info.signature_id);
    setConditions(clonedConditions);
    setSendToAllEmailAddresses(schedule.info.send_to_all_email_addresses);
    setInfo(schedule.info || {});

    if (schedule.info?.schedule_send_option) {
      const options = schedule.info.schedule_send_option;
      setRunOnce(options.run_once || false);
      setRunContinuously(options.run_continuously !== false);
      setRunUntil(options.run_until || false);

      if (isClone) {
        setRunDateLimit(null);
        setRunDateLimitTimeZone(null);
      } else {
        const effectiveTimeZone =
          options.run_date_limit_time_zone || schedule.time_zone;
        setRunDateLimit(
          getEmailDeliveryTime(options.run_date_limit, effectiveTimeZone)
        );
        setRunDateLimitTimeZone(effectiveTimeZone);
      }
    } else {
      setRunOnce(false);
      setRunContinuously(true);
      setRunUntil(false);
      setRunDateLimit(null);
      setRunDateLimitTimeZone(null);
    }

    setCustomAttachments(schedule.info.custom_attachments || []);
    setProviderIsConstantContact(
      schedule.email_service === CONSTANTS.EMAIL_SERVICES["CONSTANT_CONTACT"]
    );
    setScheduleName(isClone ? "" : schedule.schedule_name || "");
    setAttachments(schedule.attachments || []);
    setSmtpConfigId(schedule.info.smtp_config_id || undefined);
    setProviderIsSMTP(schedule.email_service === CONSTANTS.EMAIL_SERVICES.SMTP);
    setProviderIsOutlook(
      schedule.email_service === CONSTANTS.EMAIL_SERVICES.OUTLOOK
    );
    setOutlookAliasSelected(
      schedule.email_service === CONSTANTS.EMAIL_SERVICES.OUTLOOK
        ? schedule.info.from_address
        : null
    );
    setIsSendingToAllContacts(shouldSendToAllContacts);
    setConstantContactAccountId(
      schedule.info.constant_contact_account_id || undefined
    );
    setMailgunDomainSelected(isMailgun ? mailgunDomainSelected : null);
    setMailgunDomainOptions(isMailgun ? mailgunDomainOptions : []);

    setFilterReady(shouldSendToAllContacts);
    setCheckFromField(!isConstantContactOrSMTP);
  };

  const fetchCampaign = useCallback(async () => {
    const campaign = await CampaignService.getCampaign(campaignId);
    setSelectedTemplates(campaign.info.selected_templates || []);
  }, [campaignId]);

  const getEmailDeliveryTime = (dateTimeStr, timeZone) => {
    if (isClone) {
      return null;
    }
    if (dateTimeStr) {
      const tz = timeZone || "UTC";
      const localTime = moment.utc(dateTimeStr).tz(tz);
      return moment.utc(localTime.format("YYYY-MM-DD HH:mm:ss"));
    } else {
      return moment().utc().startOf("day");
    }
  };

  const setTemplateOptions = useCallback(() => {
    if (!templates) return;

    const templateOptions = templates
      .filter(t => t.value !== "any")
      .map(template => {
        let disabled = false;
        let description = null;

        if (provider) {
          disabled = templateDisabled(template, provider, emailTemplateId);
          description = disabled ? "Disabled for selected provider" : null;
        }

        if (!template.enabled) {
          disabled = true;
          description = "Empty template";
        }

        if (disabled && emailTemplateId === template.id) {
          setEmailTemplateId(null);
        }

        return {
          ...template,
          disabled,
          description,
        };
      });
    setEmailTemplateOptions(templateOptions);
  }, [templates, provider, emailTemplateId]);

  const fetchTemplates = useCallback(async () => {
    try {
      let templates = await EmailTemplateService.getEmailTemplatesForFilters();
      templates = templates.map(template => ({
        id: template.id,
        key: template.id,
        value: template.id,
        text: template.name,
        info: template.info,
        enabled: template.enabled,
      }));

      setTemplates(templates);
    } catch (error) {
      console.error("Error fetching templates:", error);
    }
  }, []);

  const validCondition = useCallback(condition => {
    if (!condition) return false;

    if (
      !condition.filter_context &&
      !condition.filter_operator &&
      condition.filter_operand2 === undefined
    ) {
      return true;
    }

    if (!condition.filter_context || !condition.filter_operator) return false;
    if (condition.filter_operand2 === undefined) return false;
    if (condition.filter_operand2 === "") return false;
    return !(
      Array.isArray(condition.filter_operand2) &&
      condition.filter_operand2.length === 0
    );
  }, []);

  const updateFilterReady = useCallback(() => {
    if (!isSendingToAllContacts) {
      const ready =
        conditions.length > 0 && conditions.every(cond => validCondition(cond));
      setFilterReady(ready);
    }
  }, [conditions, isSendingToAllContacts, validCondition]);

  const addFilter = useCallback(() => {
    if (!isSendingToAllContacts) {
      setConditions(prevConditions => [...prevConditions, { id: uuidv4() }]);
    }
  }, [isSendingToAllContacts]);

  const fetchCampaignStatuses = async () => {
    let statuses = await CampaignService.getCampaignStatuses();
    let campaignStatusOptions = statuses.map(status => ({
      key: status.id,
      value: status.id,
      text: status.name,
    }));
    campaignStatusOptions = sortBy(campaignStatusOptions, "text");
    campaignStatusOptions.unshift({
      id: null,
      info: null,
      key: null,
      text: "None/Unset",
      value: null,
    });
    setCampaignStatusOptions(campaignStatusOptions);
  };

  const fetchTags = async model => {
    let tags = await TagService.getTags({ model: model });
    if (model == "contact") {
      setContactTagOptions(tags);
    } else if (model == "CampaignTargetCall") {
      setCallTagOptions(tags);
    } else {
      setEntityTagOptions(tags);
    }
  };

  const fetchEmailFooters = async () => {
    let footers = await EmailFooterService.getEmailFootersForFilters();
    let footerOptions = footers.map(footer => ({
      key: footer.id,
      value: footer.id,
      text: footer.name,
    }));
    footerOptions.unshift({ key: null, value: null, text: "None" });
    setFooterOptions(footerOptions);
  };

  const fetchEmailSignatures = async () => {
    let signature = await EmailSignatureService.getEmailSignaturesForFilters();
    let signatureOptions = signature.map(signature => ({
      key: signature.id,
      value: signature.id,
      text: signature.name,
    }));
    signatureOptions.unshift({ key: null, value: null, text: "None" });
    setSignatureOptions(signatureOptions);
  };

  const fetchUsers = async () => {
    const fetchedUsers = await UserService.getUsers();
    setUsers(fetchedUsers);
  };

  const uploadFiles = async () => {
    if (files !== undefined && files.length > 0) {
      await uploadManager.uploadFiles(files);
      return uploadManager.uploads.map(f => f.attachment);
    } else {
      return [];
    }
  };

  const handleFileProgress = files => {
    setFiles(files);
  };

  const handleFooterChange = async (e, { value }) => {
    let footerId = value;
    if (value == "") {
      footerId = null;
    }
    setFooterId(footerId);
  };

  const handleSignatureChange = async (e, { value }) => {
    let signatureId = value;
    if (value == "") {
      signatureId = null;
    }
    setSignatureId(signatureId);
  };
  const handleChange = (event, data) => {
    const value = data.type === "checkbox" ? data.checked : data.value;
    const name = data.name;
    // Update the specific state based on the field name
    switch (name) {
      case "status":
        setStatus(value);
        break;
      case "scheduleName":
        setScheduleName(value);
        break;
      case "fromAddress":
        setFromAddress(value);
        break;
      case "cc":
        setCc(value);
        break;
      case "bcc":
        setBcc(value);
        break;
      case "ccAccountManagers":
        setCcAccountManagers(value);
        break;
      case "ccExternalAccountManagers":
        setCcExternalAccountManagers(value);
        break;
      case "mailgunDomainSelected":
        setMailgunDomainSelected(value);
        break;
      case "outlookAliasSelected":
        setOutlookAliasSelected(value);
        break;
      case "sendToAllEmailAddresses":
        setSendToAllEmailAddresses(value);
        break;
      default:
        console.warn(`No state setter found for field: ${name}`);
    }
  };

  const handleSendingToAllContactsToggle = (event, data) => {
    const isChecked = data.checked;
    setIsSendingToAllContacts(isChecked);

    if (isChecked) {
      setConditions([]);
      setFilterReady(true);
    } else {
      const newCondition = { id: uuidv4() };
      setConditions([newCondition]);
    }
  };

  const handleSelect = (e, { name, value }) => {
    switch (name) {
      case "constantContactAccountId":
        setConstantContactAccountId(value);
        break;
      default:
        console.warn(`No state setter found for field: ${name}`);
    }
  };

  const validateFields = async () => {
    let errors = {};

    if (checkFromField && !Validate.emailUsername(fromAddress)) {
      errors.fromAddress = 'Invalid value in "From" field';
    }
    if (!senderName) {
      errors.senderName = "Sender Name is required";
    }
    if (!scheduleName) {
      errors.scheduleName = "Schedule Name is required";
    }
    if (!sendNow && !emailDeliveryTime) {
      errors.emailDeliveryTime =
        "Delivery Time & Date is required when Send Later is selected";
    }
    if (runUntil && !runDateLimit) {
      errors.runDateLimit =
        "Schedule Time & Date Limit is required when Run Until is selected";
    }
    return errors;
  };

  const handleSubmit = async () => {
    try {
      const errors = await validateFields();
      if (Object.keys(errors).length > 0) {
        setSubmitErrors(errors);
        setIsSubmitting(false);
        throw new Error(
          typeof errors === "string"
            ? errors
            : Object.values(errors).join(", ") || "Validation failed"
        );
      }
      setIsSubmitting(true);
      setSubmitErrors({});
      setFieldErrors({});
      let submissionData = {
        status,
        time_zone: timeZone,
        sender_name: senderName,
        email_service: provider,
        schedule_name: scheduleName,
        conditions,
        email_template_id: emailTemplateId,
        attachments: await uploadFiles(),
        is_sending_to_all_contacts: isSendingToAllContacts,
        email_delivery_time: sendNow
          ? moment.utc()
          : convertLocalDisplayToUTC(emailDeliveryTime, timeZone) ||
            moment.utc(),
      };

      let finalFromAddress = fromAddress;
      if (provider === CONSTANTS.EMAIL_SERVICES.MAILGUN) {
        finalFromAddress = `${fromAddress}@${mailgunDomainSelected}`;
      } else if (providerIsOutlook) {
        finalFromAddress = outlookAliasSelected;
      } else if (providerIsConstantContact) {
        const selectedAccount = constantContactAccounts.find(
          account => account.value === constantContactAccountId
        );
        if (!selectedAccount?.disabled) {
          finalFromAddress = selectedAccount.text;
        }
      }

      submissionData.info = {
        from_address: finalFromAddress,
        cc,
        bcc,
        cc_account_managers: ccAccountManagers,
        cc_external_account_managers: ccExternalAccountManagers,
        custom_attachments: customAttachments,
        send_to_all_email_addresses: sendToAllEmailAddresses,
        smtp_config_id,
        constant_contact_account_id: constantContactAccountId,
        footer_id: footerId || null,
        signature_id: signatureId,
        send_now: sendNow,
        schedule_send_option: {
          run_once: runOnce,
          run_continuously: runContinuously,
          run_until: runUntil,
          run_date_limit: runDateLimit
            ? convertLocalDisplayToUTC(
                runDateLimit,
                runDateLimitTimeZone || timeZone
              ).format("YYYY-MM-DDTHH:mm:ss[Z]")
            : runDateLimit,
          run_date_limit_time_zone: runDateLimitTimeZone,
        },
      };

      if (scheduleId) {
        await CampaignScheduleService.editSchedule(
          campaignId,
          scheduleId,
          submissionData
        );
      } else {
        await CampaignScheduleService.createSchedule(
          campaignId,
          submissionData
        );
      }

      setIsSubmitting(false);
      if (onSuccess) {
        onSuccess();
      }
      if (onClose) {
        onClose();
      }
    } catch (error) {
      setIsSubmitting(false);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to save schedule";
      setSubmitErrors({ global: errorMessage });
      console.error("Submit error:", error);
      throw error;
    }
  };
  const cleanTemplateConditions = () => {
    const cleanedCond = conditions.filter(t => t != null);
    const cleanedConditions = cleanedCond.map(cond => ({
      id: uuidv4(),
      filter_context: cond.filter_context || undefined,
      filter_operand1: cond.filter_operand1 || undefined,
      filter_operator: cond.filter_operator || undefined,
      filter_operand2: cond.filter_operand2 || undefined,
    }));
    return cleanedConditions;
  };

  const handleDrop = files => {
    if (!uploadManager) {
      const newUploadManager = new UploadManager();
      newUploadManager.addProgressListener(handleFileProgress);
      setUploadManager(newUploadManager);
    }

    setFiles(prevFiles => {
      const existingFiles = prevFiles || [];
      return [...existingFiles, ...files];
    });
  };

  const deleteFile = fileIndex => {
    setFiles(prevFiles => {
      const newFiles = [...prevFiles];
      newFiles.splice(fileIndex, 1);
      return newFiles;
    });
  };

  const handleNameChange = event => {
    setSenderName(event.target.value);
  };

  const handleEmailDeliveryTimeChange = (field, value) => {
    switch (field) {
      case "sendNow":
        setSendNow(value);
        if (value) {
          setEmailDeliveryTime(getEmailDeliveryTime());
          setTimeZone(getUserTimeZone());
        }
        break;
      case "timeZone":
        // When timezone changes, adjust display to maintain same UTC time
        if (emailDeliveryTime && !sendNow) {
          const adjustedTime = adjustDisplayTimeForTimezoneChange(
            emailDeliveryTime,
            timeZone,
            value
          );
          setEmailDeliveryTime(adjustedTime);
        }
        setTimeZone(value);
        break;
      case "emailDeliveryTime":
        setEmailDeliveryTime(value);
        break;
      default:
        console.warn(`No handler found for field: ${field}`);
    }
  };

  const checkAttachmentArray = obj => {
    if (obj.custom_attachment_uri) {
      return true;
    } else {
      return false;
    }
  };

  const checkIfCanValidate = useCallback(() => {
    const isConditionsFilled = isSendingToAllContacts
      ? true
      : conditions.length > 0;
    if (
      emailTemplateId &&
      isConditionsFilled &&
      customAttachments.every(checkAttachmentArray)
    ) {
      setValidateDisabled(false);
    } else {
      setValidateDisabled(true);
    }
  }, [isSendingToAllContacts, conditions, emailTemplateId, customAttachments]);

  const handleTemplateSelect = (e, data) => {
    setEmailTemplateId(data.value);
  };

  const addAttachment = () => {
    const newCustomAttachments = [...customAttachments, {}];
    setCustomAttachments(newCustomAttachments);
  };

  const removeAttachment = attachmentIdx => {
    const newCustomAttachments = [...customAttachments];
    newCustomAttachments.splice(attachmentIdx, 1);
    setCustomAttachments(newCustomAttachments);
  };

  const renderAttachments = () => {
    return (
      <>
        {attachments.length > 0 ? (
          <Form.Field label="Previous Attachments" />
        ) : null}
        <List relaxed>
          {attachments.length > 0
            ? attachments.map((file, i) => {
                return <List.Item key={i}>{file.file_name}</List.Item>;
              })
            : null}
        </List>
        {attachments.length > 0 ? (
          <Form.Field label="New Attachments" />
        ) : (
          <Form.Field label="Attachments" />
        )}
        {isSubmitting && files && files.every(f => f.file) ? (
          <UploadProgressRing uploads={files} />
        ) : (
          <List relaxed>
            {!!files
              ? files.map((file, i) =>
                  file !== undefined ? (
                    <List.Item key={i}>
                      <List.Content floated="right">
                        <Icon name="delete" onClick={() => deleteFile(i)} />
                      </List.Content>
                      {file.name}
                    </List.Item>
                  ) : null
                )
              : null}
          </List>
        )}
        <Dropzone onDrop={files => handleDrop(files)}>
          {({ getRootProps, getInputProps }) => (
            <Segment style={{ border: "dashed 2px grey", textAlign: "center" }}>
              <div {...getRootProps()}>
                <input {...getInputProps()} />
                <p>Drag 'n' drop some files here, or click to select files</p>
              </div>
            </Segment>
          )}
        </Dropzone>
        {customAttachments.map((attachment, attachmentIdx) => (
          <CustomAttachmentForm
            key={attachmentIdx}
            attachmentIdx={attachmentIdx}
            attachment={attachment}
            removeAttachment={removeAttachment}
            customAttachmentList={customAttachments}
            scheduleConditions={conditions}
            emailTemplateId={emailTemplateId}
            campaignId={campaignId}
            scheduleId={scheduleId}
            validateDisabled={validateDisabled}
            checkIfCanValidate={checkIfCanValidate}
            sendToAllEmailAddresses={sendToAllEmailAddresses}
          />
        ))}
        <Button
          basic
          color="grey"
          size="mini"
          content="Add Custom Attachment"
          type="button"
          onClick={() => addAttachment()}
        />
      </>
    );
  };

  const removeFilter = useCallback(
    conditionIdx => {
      if (!isSendingToAllContacts) {
        setConditions(prevConditions => {
          const newConditions = [...prevConditions];
          newConditions.splice(conditionIdx, 1);
          return newConditions;
        });
      }
    },
    [isSendingToAllContacts]
  );

  const makeFilterForm = useCallback(
    (condition, conditionIdx) => {
      return (
        <OutreachFiltersForm
          key={condition.id}
          uuid={condition.id}
          campaignStatuses={campaignStatusOptions}
          emailTemplateOptions={templates}
          userOptions={userOptions}
          campaignId={campaignId}
          conditions_list={conditions}
          condition={condition}
          contactTagOptions={contactTagOptions}
          entityTagOptions={entityTagOptions}
          callTagsOptions={callTagOptions}
          conditionIdx={conditionIdx}
          removeFilterElement={removeFilter}
          checkIfCanValidate={checkIfCanValidate}
          emailDomainFilter={true}
          fetchEntities={fetchEntities}
          isFilterReady={updateFilterReady}
        />
      );
    },
    [
      campaignStatusOptions,
      templates,
      userOptions,
      campaignId,
      conditions,
      contactTagOptions,
      entityTagOptions,
      callTagOptions,
      removeFilter,
      checkIfCanValidate,
      fetchEntities,
      updateFilterReady,
    ]
  );

  const ConditionsRender = (
    <>
      <Form.Field label="Conditions" required style={{ marginTop: "10px" }} />
      <div className="and-filter">
        {conditions.map((condition, conditionIdx) => {
          if (emailTemplateOptions.length > 0) {
            return makeFilterForm(condition, conditionIdx);
          } else {
            return null;
          }
        })}
      </div>
      {!isSendingToAllContacts && (
        <Button
          basic
          color="grey"
          size="mini"
          content="Add Filter"
          name="fromAddress"
          onClick={addFilter}
          type="button"
        />
      )}
    </>
  );

  let usernameField = (
    <>
      <Form.Input
        label="From"
        placeholder="ex. username"
        onChange={handleChange}
        name="fromAddress"
        value={fromAddress}
        error={!!errorMessages.fromAddress}
      />
      <div style={{ paddingTop: "2rem", marginLeft: "-1rem" }}>
        <p>{`@${fromDomain}`}</p>
      </div>
    </>
  );

  if (providerIsSMTP) {
    usernameField = (
      <Form.Select
        selection
        label="Choose Account"
        options={smtpOptions}
        value={smtp_config_id}
        onChange={handleAccountChange}
        required
      />
    );
  } else if (provider === CONSTANTS.EMAIL_SERVICES.MAILGUN) {
    usernameField = (
      <>
        <Form.Input
          required
          label="From"
          placeholder="ex. username"
          onChange={handleChange}
          name="fromAddress"
          value={fromAddress}
          error={!!errorMessages.fromAddress}
        />

        <Form.Select
          name="mailgunDomainSelected"
          selection
          label="Select a domain"
          options={mailgunDomainOptions}
          onChange={handleChange}
          value={mailgunDomainSelected}
          required
        />
      </>
    );
  } else if (providerIsOutlook) {
    usernameField = (
      <Form.Select
        name="outlookAliasSelected"
        selection
        label="Select an alias"
        options={outlookEmailAliases}
        onChange={handleChange}
        value={outlookAliasSelected}
        required
      />
    );
  }

  const filteredEmailTemplateOptions =
    selectedTemplates && selectedTemplates.length > 0
      ? emailTemplateOptions.filter(option =>
          selectedTemplates.includes(option.value)
        )
      : emailTemplateOptions;

  const isReady = () => {
    const hasBasicFields =
      !!scheduleName && !!provider && !!senderName && !!emailTemplateId;
    if (!hasBasicFields) return false;

    let hasValidFromAddress = true;
    if (provider) {
      if (providerIsOutlook) {
        hasValidFromAddress = !!outlookAliasSelected;
      } else if (providerIsSMTP) {
        hasValidFromAddress = !!smtp_config_id;
      } else if (providerIsMailgun) {
        hasValidFromAddress = !!fromAddress && !!mailgunDomainSelected;
      } else if (providerIsConstantContact) {
        hasValidFromAddress = !!constantContactAccountId;
      } else {
        hasValidFromAddress = !!fromAddress;
      }
    }
    if (!hasValidFromAddress) return false;

    if (!sendNow && !emailDeliveryTime) {
      return false;
    }

    if (runUntil && !runDateLimit) {
      return false;
    }

    if (isSendingToAllContacts) {
      return true; // No need to check filters if sending to all contacts
    } else {
      return (
        conditions.length > 0 &&
        conditions.every(cond => {
          return (
            cond &&
            cond.filter_context &&
            cond.filter_operator &&
            cond.filter_operand2 !== undefined &&
            cond.filter_operand2 !== "" &&
            !(
              Array.isArray(cond.filter_operand2) &&
              cond.filter_operand2.length === 0
            )
          );
        })
      );
    }
  };

  const handleProviderChange = async (event, data) => {
    const isConstantContact =
      data.value === CONSTANTS.EMAIL_SERVICES.CONSTANT_CONTACT;
    const isSMTP = data.value === CONSTANTS.EMAIL_SERVICES.SMTP;
    const isOutlook = data.value === CONSTANTS.EMAIL_SERVICES.OUTLOOK;
    const isMailgun = data.value === CONSTANTS.EMAIL_SERVICES.MAILGUN;
    const isConstantContactOrSMTP = isConstantContact || isSMTP;

    if (isMailgun) {
      const { domainOptions, defaultDomain } = await fetchMailgunDomains();
      setProvider(data.value);
      setProviderIsConstantContact(isConstantContact);
      setProviderIsSMTP(isSMTP);
      setProviderIsOutlook(isOutlook);
      setProviderIsMailgun(true);
      setFromAddress("");
      setCheckFromField(!isConstantContactOrSMTP);
      setMailgunDomainOptions(domainOptions);
      setMailgunDomainSelected(defaultDomain);
    } else {
      setProvider(data.value);
      setProviderIsConstantContact(isConstantContact);
      setProviderIsSMTP(isSMTP);
      setProviderIsOutlook(isOutlook);
      setProviderIsMailgun(false);
      setFromAddress("");
      setCheckFromField(!isConstantContactOrSMTP);
      setMailgunDomainOptions([]);
      setMailgunDomainSelected(null);
    }
    await fetchEmailServicesOptions(data.value);

    handleAccountChange(null, { value: smtp_config_id });
  };

  const ready = isReady();

  useEffect(() => {
    if (!isSendingToAllContacts) {
      const hasValidConditions =
        conditions.length > 0 && conditions.every(cond => validCondition(cond));
      setFilterReady(hasValidConditions);
    }
    checkIfCanValidate();
  }, [conditions, isSendingToAllContacts, validCondition, checkIfCanValidate]);

  useEffect(() => {
    if (templates && templates.length > 0) {
      setTemplateOptions();
    }
  }, [templates, setTemplateOptions]);

  useEffect(() => {
    let isMounted = true;

    const initializeForm = async () => {
      if (isMounted) {
        if (campaignId && scheduleId) {
          await fetchSchedule(campaignId, scheduleId, isClone);
        }

        fetchTags("contact");
        fetchTags("entity");
        fetchTags("CampaignTargetCall");
        fetchTemplates();
        fetchCampaignStatuses();
        fetchCampaign();
        setupTimezones();
        fetchEmailServices();
        fetchEmailFooters();
        fetchEmailSignatures();
        fetchUsers();
        fetchEntities();
      }
    };

    initializeForm();

    return () => {
      isMounted = false;
    };
  }, [campaignId, scheduleId, isClone]);

  const validationTimeoutRef = useRef(null);

  useEffect(() => {
    if (validationTimeoutRef.current) {
      clearTimeout(validationTimeoutRef.current);
    }

    // Set new timeout - only validate after user stops typing for 500ms
    validationTimeoutRef.current = setTimeout(async () => {
      const errors = await validateFields();
      setFieldErrors(errors);
    }, 500);

    return () => {
      if (validationTimeoutRef.current) {
        clearTimeout(validationTimeoutRef.current);
      }
    };
  }, [
    scheduleName,
    senderName,
    fromAddress,
    mailgunDomainSelected,
    constantContactAccountId,
    smtp_config_id,
    outlookAliasSelected,
    provider,
    sendNow,
    emailDeliveryTime,
    runUntil,
    runDateLimit,
  ]);

  const handleScheduleOptionChange = useCallback(option => {
    switch (option) {
      case "continuous":
        setRunContinuously(true);
        setRunOnce(false);
        setRunUntil(false);
        setRunDateLimit(null);
        setRunDateLimitTimeZone(null);
        break;
      case "once":
        setRunContinuously(false);
        setRunOnce(true);
        setRunUntil(false);
        setRunDateLimit(null);
        setRunDateLimitTimeZone(null);
        break;
      case "date":
        setRunContinuously(false);
        setRunOnce(false);
        setRunUntil(true);
        break;
    }
  }, []);

  const handleRunDateLimitChange = useCallback(value => {
    setRunDateLimit(value);
  }, []);

  const handleRunDateLimitTimeZoneChange = useCallback(
    value => {
      // When timezone changes, adjust display to maintain same UTC time
      if (runDateLimit) {
        const adjustedTime = adjustDisplayTimeForTimezoneChange(
          runDateLimit,
          runDateLimitTimeZone || timeZone,
          value
        );
        setRunDateLimit(adjustedTime);
      }
      setRunDateLimitTimeZone(value);
    },
    [runDateLimit, runDateLimitTimeZone, timeZone]
  );

  return (
    <>
      <RuvixxForm
        ready={ready}
        onSubmit={handleSubmit}
        onSuccess={onSuccess}
        onCancel={onClose}
        error={Object.keys(errorMessages).length > 0}
        errorMessage={Object.values(errorMessages).find(Boolean) || ""}
        extraButtons={[
          <EmailableContactsModal
            disabled={!ready}
            campaignId={campaignId}
            data={{
              conditions: conditions,
              email_template_id: emailTemplateId,
              sending_to_all_contacts: isSendingToAllContacts,
              info: {
                send_to_all_email_addresses: sendToAllEmailAddresses,
              },
            }}
          />,
        ]}
        successHeader="Email Schedule Created"
        successMessage="Successfully created a new schedule"
      >
        {!providerIsConstantContact && (
          <Form.Checkbox
            toggle
            name="status"
            checked={status}
            onChange={handleChange}
            label={`Status [${status ? "Active" : "Inactive"}]`}
            required
          />
        )}
        <Form.Input
          name="scheduleName"
          value={scheduleName}
          onChange={handleChange}
          label="Schedule Name"
          placeholder="Type a unique Schedule Name"
          required
          error={!!errorMessages.scheduleName}
        />
        <Form.Group>
          <Form.Select
            selection
            label="Email Provider"
            placeholder="Select an Email Provider"
            options={emailServices}
            onChange={handleProviderChange}
            value={provider}
            required
          />
          {providerIsConstantContact && (
            <Form.Select
              selection
              name={"constantContactAccountId"}
              label="Constant Contact Account"
              placeholder="Select Account"
              options={constantContactAccounts}
              onChange={handleSelect}
              value={constantContactAccountId}
              required
            />
          )}
        </Form.Group>
        {!providerIsConstantContact && (
          <>
            <Form.Group widths="equal">
              <Form.Input
                required
                label="Sender Name"
                placeholder="Type in Sender Name"
                onChange={handleNameChange}
                name="senderName"
                value={senderName}
                error={!!errorMessages.senderName}
              />
              {usernameField}
            </Form.Group>
            <Accordion active={ccMenuOpen} as={Form.Field}>
              <Accordion.Title
                active={ccMenuOpen}
                index={0}
                onClick={() => setCcMenuOpen(!ccMenuOpen)}
              >
                <Icon name="dropdown" />
                CC/BCC Options
              </Accordion.Title>
              <Accordion.Content active={ccMenuOpen}>
                <Form.Group>
                  <Form.Checkbox
                    toggle
                    name="ccAccountManagers"
                    checked={ccAccountManagers}
                    onChange={handleChange}
                    label="CC Account Managers"
                  />
                  <Form.Checkbox
                    toggle
                    name="ccExternalAccountManagers"
                    checked={ccExternalAccountManagers}
                    onChange={handleChange}
                    label="CC External Account Managers"
                  />
                </Form.Group>
                <Form.Input
                  label="CC"
                  placeholder="email1@test.com,email2@test.com"
                  onChange={handleChange}
                  name="cc"
                  value={cc}
                />
                <Form.Input
                  label="BCC"
                  placeholder="email1@test.com,email2@test.com"
                  onChange={handleChange}
                  name="bcc"
                  value={bcc}
                />
              </Accordion.Content>
            </Accordion>
          </>
        )}
        {!providerIsConstantContact && (
          <Grid
            columns="equal"
            style={{ paddingLeft: "0.5em", paddingBottom: "2em" }}
          >
            <Grid.Row>
              <Grid.Column>
                <EmailDeliveryTime
                  timeZone={timeZone}
                  timeZones={timeZones}
                  sendNow={sendNow}
                  emailDeliveryTime={emailDeliveryTime}
                  setEmailDeliveryTime={handleEmailDeliveryTimeChange}
                  error={!!errorMessages.emailDeliveryTime}
                />
              </Grid.Column>
              <Grid.Column>
                <EmailScheduleSendOptionForm
                  timeZone={timeZone}
                  timeZones={timeZones}
                  runOnce={runOnce}
                  runUntil={runUntil}
                  runContinuously={runContinuously}
                  runDateLimit={runDateLimit}
                  runDateLimitTimeZone={runDateLimitTimeZone}
                  onScheduleOptionChange={handleScheduleOptionChange}
                  onRunDateLimitChange={handleRunDateLimitChange}
                  onRunDateLimitTimeZoneChange={
                    handleRunDateLimitTimeZoneChange
                  }
                  error={!!errorMessages.runDateLimit}
                />
              </Grid.Column>
            </Grid.Row>
          </Grid>
        )}
        <Form.Group widths="equal">
          <Form.Select
            required
            search
            options={filteredEmailTemplateOptions}
            name="email_template_id"
            placeholder="Select an Email Template"
            value={emailTemplateId}
            label="Email Template"
            onChange={handleTemplateSelect}
          />
          <Form.Select
            search
            clearable
            label="Email Footer"
            placeholder="Select an Email Footer"
            value={footerId}
            onChange={handleFooterChange}
            options={footerOptions}
          />
          <Form.Select
            search
            clearable
            label="Email Signature"
            placeholder="Select an Email Signature"
            value={signatureId}
            onChange={handleSignatureChange}
            options={signatureOptions}
          />
        </Form.Group>
        {!providerIsConstantContact && renderAttachments()}
        <Form.Checkbox
          toggle
          name="sendToAllEmailAddresses"
          checked={sendToAllEmailAddresses}
          onChange={handleChange}
          label={`Send to all enabled emails addresses per contact [${
            sendToAllEmailAddresses ? "Active" : "Inactive"
          }]`}
          required
        />
        <Form.Checkbox
          toggle
          name="isSendingToAllContacts"
          checked={isSendingToAllContacts}
          onChange={handleSendingToAllContactsToggle}
          label={`Send to all contacts [${
            isSendingToAllContacts ? "Active" : "Inactive"
          }]`}
          required
        />
        {!isSendingToAllContacts ? ConditionsRender : null}
      </RuvixxForm>
    </>
  );
};

CampaignScheduleForm.propTypes = {
  campaignId: PropTypes.number.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
  newSchedule: PropTypes.bool,
  scheduleId: PropTypes.number,
  isClone: PropTypes.bool,
};

export default CampaignScheduleForm;
